/**
 * Where to send someone after they sign in.
 *
 * Only same-origin, absolute paths are allowed. Anything else (an absolute URL,
 * a protocol-relative "//evil.com", or a backslash variant that some browsers
 * normalise to "//") is rejected, so the sign-in page cannot be turned into an
 * open redirect.
 *
 * The check is structural, not a list of bad shapes. The value is resolved
 * against a base nobody owns, and it is accepted only if it lands on that same
 * base. A pattern check cannot do this: the URL parser strips ASCII tab, line
 * feed and carriage return BEFORE it parses, so "/<tab>/evil.com" passes a
 * literal "//" test and still navigates to evil.com. Control characters are
 * therefore removed first, and the parser then answers the only question that
 * matters, which is which host the browser would actually go to.
 *
 * The path and its query come back separately because `NextURL.pathname` must
 * never be handed a string containing "?".
 */

/** A base nobody can register: .invalid is reserved by RFC 2606. */
const INTERNAL_BASE = "http://internal.invalid";

/** Removes every ASCII control character, including the three the parser drops. */
function stripControlCharacters(value: string): string {
  let cleaned = "";
  for (const character of value) {
    if (character.charCodeAt(0) >= 0x20) {
      cleaned += character;
    }
  }
  return cleaned;
}

export function parseRedirectTo(value: string | null | undefined): { pathname: string; search: string } | null {
  if (!value) return null;

  const cleaned = stripControlCharacters(value);
  if (!cleaned.startsWith("/")) return null;

  let resolved: URL;
  try {
    resolved = new URL(cleaned, INTERNAL_BASE);
  } catch {
    return null;
  }

  // Anything that named a host of its own, by any spelling, has moved off the
  // base by now: "//host", "/\host", a full absolute URL, and every form the
  // parser normalises into one.
  if (resolved.origin !== INTERNAL_BASE) return null;

  return { pathname: resolved.pathname, search: resolved.search };
}
